import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getMessages, sendMessage } from '../services/messageService';
import './ChatPanel.css';

const POLL_INTERVAL = 30000;

const formatDateLabel = (dateStr) => {
  const d = new Date(dateStr);
  const now = new Date();
  const isToday =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (isToday) return 'Today';
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday =
    d.getFullYear() === yesterday.getFullYear() &&
    d.getMonth() === yesterday.getMonth() &&
    d.getDate() === yesterday.getDate();
  if (isYesterday) return 'Yesterday';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatTime = (dateStr) => {
  return new Date(dateStr).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

const ChatPanel = ({ coachClientId, currentUserId, otherName, otherPicture, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState('');
  const scrollRef = useRef(null);
  const pollRef = useRef(null);
  const initialScrollDone = useRef(false);

  /** Scroll only the message list — avoid scrollIntoView (it scrolls the whole page). */
  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const useSmooth = initialScrollDone.current;
    initialScrollDone.current = true;
    if (useSmooth && typeof el.scrollTo === 'function') {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    } else {
      el.scrollTop = el.scrollHeight;
    }
  }, []);

  const fetchMessages = useCallback(async ({ append = false } = {}) => {
    try {
      const data = await getMessages(coachClientId, { limit: 50 });
      setMessages(data.messages || []);
      setHasMore(data.hasMore || false);
    } catch {
      /* silent poll failure */
    } finally {
      setLoading(false);
    }
  }, [coachClientId]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  useEffect(() => {
    if (!loading && messages.length > 0) {
      scrollToBottom();
    }
  }, [loading, messages.length, scrollToBottom]);

  useEffect(() => {
    pollRef.current = setInterval(() => fetchMessages(), POLL_INTERVAL);
    const onFocus = () => fetchMessages();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(pollRef.current);
      window.removeEventListener('focus', onFocus);
    };
  }, [fetchMessages]);

  const loadOlder = async () => {
    if (!hasMore || messages.length === 0) return;
    const oldest = messages[0].createdAt;
    try {
      const data = await getMessages(coachClientId, { before: oldest, limit: 50 });
      setMessages(prev => [...(data.messages || []), ...prev]);
      setHasMore(data.hasMore || false);
    } catch { /* ignore */ }
  };

  const handleSend = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const msg = await sendMessage(coachClientId, text);
      setMessages(prev => [...prev, msg]);
      setDraft('');
      setTimeout(scrollToBottom, 50);
    } catch {
      /* keep draft so user can retry */
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  let lastDateLabel = '';

  return (
    <div className="chat-panel">
      <div className="chat-panel-header">
        <div className="chat-panel-header-info">
          {otherPicture ? (
            <img src={otherPicture} alt="" className="chat-panel-avatar" />
          ) : (
            <div className="chat-panel-avatar-placeholder">
              {(otherName || '?')[0].toUpperCase()}
            </div>
          )}
          <span className="chat-panel-name">{otherName || 'Chat'}</span>
        </div>
        {onClose && (
          <button type="button" className="chat-panel-close" onClick={onClose} aria-label="Close chat">
            <i className="fas fa-times"></i>
          </button>
        )}
      </div>

      <div className="chat-panel-messages" ref={scrollRef}>
        {loading && (
          <div className="chat-panel-loading">
            <i className="fas fa-spinner fa-spin"></i>
          </div>
        )}
        {!loading && messages.length === 0 && (
          <div className="chat-panel-empty">
            <p>No messages yet. Say hello!</p>
          </div>
        )}
        {hasMore && !loading && (
          <button type="button" className="chat-panel-load-more" onClick={loadOlder}>
            Load older messages
          </button>
        )}
        {messages.map((msg) => {
          const dateLabel = formatDateLabel(msg.createdAt);
          let showDate = false;
          if (dateLabel !== lastDateLabel) {
            showDate = true;
            lastDateLabel = dateLabel;
          }
          const isSelf = String(msg.senderId) === String(currentUserId);
          return (
            <React.Fragment key={msg._id}>
              {showDate && <div className="chat-panel-date-divider">{dateLabel}</div>}
              <div className={`chat-bubble ${isSelf ? 'chat-bubble-self' : 'chat-bubble-other'}`}>
                <p className="chat-bubble-body">{msg.body}</p>
                <span className="chat-bubble-time">{formatTime(msg.createdAt)}</span>
              </div>
            </React.Fragment>
          );
        })}
      </div>

      <div className="chat-panel-input-row">
        <textarea
          className="chat-panel-input"
          placeholder="Type a message..."
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          onMouseDown={(e) => {
            if (document.activeElement !== e.currentTarget) {
              e.preventDefault();
              e.currentTarget.focus({ preventScroll: true });
            }
          }}
          rows={1}
          maxLength={2000}
          disabled={sending}
        />
        <button
          type="button"
          className="chat-panel-send"
          onClick={handleSend}
          disabled={!draft.trim() || sending}
          aria-label="Send message"
        >
          <i className={`fas ${sending ? 'fa-spinner fa-spin' : 'fa-paper-plane'}`}></i>
        </button>
      </div>
    </div>
  );
};

export default ChatPanel;
